import { useReunion } from './state/useReunion';
import { AdminOverlay } from './components/AdminOverlay';
import { DevPill } from './components/DevPill';
import { EmailToast } from './components/EmailToast';
import { Footer } from './components/Footer';
import { Hero } from './components/Hero';
import { COL } from './components/primitives';
import { PhaseStub } from './components/PhaseStub';
import { SectionKnown } from './components/SectionKnown';
import { SectionOpen } from './components/SectionOpen';
import { SectionRespond } from './components/SectionRespond';
import { SectionWhosIn } from './components/SectionWhosIn';
import { TopBar } from './components/TopBar';

export default function App() {
  const api = useReunion();
  const { ready, state, view, actions } = api;

  if (!ready || !view) {
    // Local backend resolves within a tick; this is a brief branded splash,
    // never a wall.
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 13, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--muted-2)' }}>
          water hazard '27
        </div>
      </div>
    );
  }

  const { content, vis, phase } = view;
  // Real contact comes from the gated config/contact doc (loaded only when
  // signed in); the bundled placeholder is just a fallback and never shows to
  // signed-out users anyway.
  const org = view.contact ?? content.organizer;

  return (
    <div style={{ minHeight: '100vh' }}>
      <TopBar signedIn={view.signedIn} householdLabel={view.householdLabel} onSignOut={actions.signOut} />

      <Hero />

      <main style={COL}>
        <SectionKnown facts={view.facts} />

        {vis('open') && <SectionOpen items={view.open} />}

        {vis('respond') && <SectionRespond api={api} />}

        {vis('whosin') && (
          <SectionWhosIn
            headsIn={view.headsIn}
            invited={view.invited}
            signedIn={view.signedIn}
            rosterNames={view.rosterNames}
            waitingCount={view.waitingCount}
          />
        )}

        {vis('lodging') && (
          <PhaseStub
            label="05 lodging"
            numeral="05 — where you'll sleep"
            title="lodging tiers"
            body="phase two section. three price tiers, a map, and a booking link per tier — same components, filled from the data layer once the destination is set."
          />
        )}

        {vis('schedule') && (
          <PhaseStub
            label="06 schedule"
            numeral="06 — the days"
            title="schedule"
            body="phase three. four days, a tee time, a hatch chart, and one dinner nobody is allowed to skip."
          />
        )}

        {vis('travel') && (
          <PhaseStub
            label="07 travel"
            numeral="07 — getting there"
            title="travel"
            body="phase three. nearest airports, who's driving past whom, and the one rental car we're sharing."
          />
        )}
      </main>

      <Footer
        organizerName={org.name.toLowerCase()}
        organizerPhone={org.phone}
        organizerPhoneHref={org.phoneHref}
        organizerEmail={org.email}
        signedIn={view.signedIn}
        isAdmin={view.isAdmin}
        onOpenAdmin={actions.openAdmin}
      />

      {state.adminOpen && (
        <AdminOverlay
          rows={state.adminRows}
          note={state.adminNote}
          newEmail={state.newEmail}
          onNewEmail={actions.onNewEmail}
          onAdd={actions.addEmail}
          onClose={actions.closeAdmin}
          detail={state.adminDetail}
          form={state.adminForm}
          setForm={actions.setAdminForm}
          weeks={content.weeks}
          window={view.window}
          travelModes={content.travelModes}
          busy={state.adminBusy}
          onOpenDetail={actions.openHouseholdDetail}
          onCloseDetail={actions.closeHouseholdDetail}
          onEditDetail={actions.editHouseholdDetail}
          onSubmitDetail={actions.submitHouseholdDetail}
        />
      )}

      {view.showNotif && view.demoCode && <EmailToast code={view.demoCode} />}

      {view.devVisible && view.demoCode && (
        <DevPill
          open={state.devOpen}
          onToggle={actions.toggleDev}
          currentPhase={phase}
          onPickPhase={actions.pickPhase}
          onReset={actions.devReset}
          onExpire={actions.devExpire}
          onAdmin={actions.devAdmin}
          demoCode={view.demoCode}
        />
      )}
    </div>
  );
}
